import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { convertIpv4, Ipv4 } from 'motion-master-client';
import { Subject, takeUntil } from 'rxjs';

import { formArrayDuplicateValueValidator, ipv4Validator } from '@app/custom-validators';
import { FormControlIpv4Component } from '@app/shared/form-control-ipv4/form-control-ipv4.component';

@Component({
  selector: 'app-form-ipv4',
  templateUrl: './form-ipv4.component.html',
  styleUrls: ['./form-ipv4.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: FormIpv4Component,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: FormIpv4Component,
      multi: true,
    },
  ],
  imports: [FormsModule, ReactiveFormsModule, FormControlIpv4Component],
})
export class FormIpv4Component implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, Validator {
  private errorMessage = 'This value will be discarded.';

  observer: MutationObserver;
  unsubscribe$ = new Subject<void>();

  @ViewChild('addressListContainer', { static: true }) addressListContainerRef: ElementRef;

  @Input()
  value: Ipv4[] = []; // List of IPv4 addresses as numbers

  get invalid() {
    return this.formArray.invalid;
  }

  get formArray() {
    return this.form.get('ipv4') as FormArray;
  }

  form = new FormGroup({
    ipv4: new FormArray([], { validators: [formArrayDuplicateValueValidator(this.errorMessage)] }),
  });

  ngAfterViewInit(): void {
    // Initialize mutation observer after the view has been
    // rendered to avoid initially selecting the first field
    this.observer = new MutationObserver((records) => {
      if (records.some((record) => record.addedNodes.length > 0)) {
        this.focusAddedNode(records[0]);
      }
      if (records.some((record) => record.removedNodes.length > 0)) {
        this.focusLastNode(records[0]);
      }
    });
    this.observer.observe(this.addressListContainerRef.nativeElement, {
      childList: true,
      subtree: false,
      attributes: false,
    });
  }

  ngOnInit(): void {
    this.formArray.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((values) => {
      this.onChange(values);
    });
    this.addControls();
  }

  ngOnDestroy(): void {
    this.observer.disconnect();
    this.unsubscribe$.next();
  }

  addControls() {
    this.value.forEach((address) => {
      this.formArray.push(new FormControl(address, [ipv4Validator]));
    });
  }

  appendControl() {
    const lastControlValue = this.formArray.at(this.formArray.length - 1)?.value ?? '0.0.0.0';
    const lastudintValue = convertIpv4(lastControlValue) as number;

    const newudintValue = lastudintValue + 1;
    const newIpv4Value = convertIpv4(newudintValue) as Ipv4;

    this.formArray.push(new FormControl(newIpv4Value, [ipv4Validator]));
  }

  removeControl(index: number) {
    this.formArray.removeAt(index);
  }

  onChange = (_value: number[]) => {};
  onTouched: () => void;
  writeValue(value: Ipv4[]): void {
    this.value = value;
    this.addControls();
  }
  registerOnChange(fn: (value: number[]) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState(_isDisabled: boolean): void {}

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.formArray.valid ? null : { invalidIpv4: true };
  }

  // Focus the new input when it's added to the DOM
  private focusAddedNode(record: MutationRecord) {
    const addedNode = record.addedNodes[0] as HTMLElement;
    if (!(addedNode instanceof HTMLElement)) {
      return;
    }
    const input = addedNode.querySelector('input') as HTMLInputElement;
    this.focusInputElement(input);
  }

  private focusLastNode(record: MutationRecord) {
    const removedNode = record.removedNodes[0] as HTMLElement;
    if (!(removedNode instanceof HTMLElement)) {
      return;
    }
    const input = this.addressListContainerRef.nativeElement.lastElementChild.querySelector(
      'input',
    ) as HTMLInputElement;
    this.focusInputElement(input);
  }

  private focusInputElement(input: HTMLInputElement) {
    if (!input) {
      return;
    }
    const startSelectIndex = input.value.lastIndexOf('.') + 1;
    const endSelectIndex = input.value.length;

    input.setSelectionRange(startSelectIndex, endSelectIndex);
    input.focus();
  }
}
